import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";
import { setFieldValue } from "../../shared/field_values";

export default class extends Controller {
  static targets = ["country", "state"];
  static values = {
    selectStateText: { type: String, default: "Select a state" },
    initialState: { type: String, default: "" },
    // Country resolution priority:
    //   1. `country` target (a `<select>` or `<input>`
    //      somewhere in scope)
    //   2. `data-state-selector-country-value` on the
    //      controller root
    //   3. "MX" fallback (we're MX-only today)
    country: { type: String, default: "MX" },
  };

  connect() {
    // Skip initialization on reconnects to avoid reloading
    // states. The latch lives on the controller INSTANCE,
    // never on a data attribute: morphdom syncs attributes
    // from the incoming server HTML, which never carries
    // the flag, so a dataset latch is wiped by every morph
    // and connect() refetched the state list each time
    // (CAL-401). A preserved element keeps this controller
    // instance; a replaced one gets a fresh instance and
    // initializes again, which is exactly the lifetime the
    // latch is trying to describe.
    //
    // Keyed on the inputs the lookup depends on rather
    // than a bare "have I run" boolean, because a boolean
    // overcorrects. Picking another saved address MORPHS
    // this element instead of replacing it, so the same
    // instance is handed a new state by the server;
    // skipping then left the select holding the previous
    // address's state (CAL-401).
    const key = this.initKey();

    if (this.initializedFor === key) return;

    this.initializedFor = key;

    if (this.hasStateTarget) {
      // Read BEFORE the list is rebuilt: `updateStates()`
      // rewrites the options, and a value read after it
      // is whatever the new list starts on. That used to
      // be read afterwards, so a reconnect with a state
      // in the select and nothing in `initialStateValue`
      // (a guest's pick kept through a customer morph,
      // CAL-522, where the server knew no state) emptied
      // it, and the address with it.
      const stateToSet =
        this.stateTarget.value || this.initialStateValue;

      if (this.currentCountry() && stateToSet) {
        this.updateStates().then(() => {
          if (!this.hasStateTarget) return;

          setFieldValue(this.stateTarget, stateToSet);
          this.stateTarget.dispatchEvent(new Event("change"));
        });
      } else {
        this.updateStates();
      }
    }
  }

  // Everything `connect()` reads to decide what to fetch
  // and which option to select. Two connects that agree on
  // all of it have nothing new to do.
  initKey() {
    return [
      this.currentCountry(),
      this.hasStateTarget ? this.stateTarget.value : "",
      this.initialStateValue,
    ].join("/");
  }

  currentCountry() {
    if (this.hasCountryTarget && this.countryTarget.value) {
      return this.countryTarget.value;
    }
    return this.countryValue;
  }

  addressSelected(event) {
    const { address } = event.detail;

    if (address.state) {
      // Store the state value to be set after loading
      this.stateTarget.dataset.initialValue = address.state;
      // Load states and then set the value
      this.updateStates().then(() => {
        this.stateTarget.value = address.state;
        this.stateTarget.dispatchEvent(new Event("change"));
      });
    }
  }

  updateStates() {
    const country = this.currentCountry();
    if (!country) {
      this.stateTarget.innerHTML = `<option value="">${this.selectStateTextValue}</option>`;
      return Promise.resolve();
    }

    // Both this controller's connect() and the
    // `addressSelected` broadcast ask for the same list on
    // a single page load, so one answer cost two round
    // trips (CAL-401). Sharing the in-flight PROMISE
    // collapses them while still handing every caller
    // something that resolves once the options are in the
    // DOM: the `.then()` chains that set the state value
    // depend on that.
    if (
      this.statesRequest &&
      this.statesRequestCountry === country
    ) {
      return this.statesRequest;
    }

    this.statesRequestCountry = country;

    // Use @rails/request.js to fetch states
    this.statesRequest = get(
      `/shared/api/states?country=${country}`
    )
      .then((response) => {
        if (response.ok) {
          return response.json.then((states) => {
            // A new copy of the same list changes no
            // answer, so the state in the select stays.
            const current = this.stateTarget.value;

            this.stateTarget.innerHTML = `<option value="">${this.selectStateTextValue}</option>`;
            Object.entries(states).forEach(([code, name]) => {
              const option = document.createElement("option");
              option.value = code;
              option.textContent = name;
              this.stateTarget.appendChild(option);
            });

            // Set the state value if we have an initial value
            if (this.stateTarget.dataset.initialValue) {
              this.stateTarget.value = this.stateTarget.dataset.initialValue;
              this.stateTarget.dispatchEvent(new Event("change"));
            } else if (current) {
              setFieldValue(this.stateTarget, current);
            }
          });
        } else if (!response.ok) {
          console.error(response);
        }
      })
      .catch((error) => {
        if (this.hasStateTarget) {
          this.stateTarget.innerHTML = `<option value="">${this.selectStateTextValue}</option>`;
          this.stateTarget.disabled = true;
        }
      })
      .finally(() => {
        // Only IN-FLIGHT requests are shared, never a
        // settled one. See the matching note in
        // `city_selector_controller`. Guarded on the
        // country so a newer request already in flight is
        // not cleared.
        if (this.statesRequestCountry === country) {
          this.statesRequest = null;
        }
      });

    return this.statesRequest;
  }
}
